/**
 * Custom Shopify Cart Drawer
 * File: assets/cart-drawer.js
 *
 * SETUP: Set your SHIPPING_PROTECTION_VARIANT_ID in the theme editor sidebar
 * (Cart Drawer section > Shipping Protection > Variant ID field)
 *
 * Pricing: All Shopify cart APIs return prices in CENTS. $24.00 = 2400.
 * The _money() helper divides by 100 automatically.
 */

const CD_CONFIG = {
  SHIPPING_PROTECTION_VARIANT_ID: 0,
  SHIPPING_PROTECTION_PRICE_CENTS: 295,
  REWARD_TIERS: [],
  FBT_PRODUCT_COUNT: 3,
  FBT_INTENT: 'related',
  CHECKOUT_BTN_TEXT: 'Checkout \u2022 {total}',
  CONTINUE_SHOPPING_URL: '/collections/all',
};

(function applySettings() {
  const s = window.cdSectionSettings;
  if (!s) return;
  if (s.tiers && s.tiers.length > 0) {
    CD_CONFIG.REWARD_TIERS = s.tiers.map(t => ({
      threshold: parseInt(t.threshold, 10), title: t.title,
      icon: t.icon || '\ud83c\udf81', imageUrl: t.imageUrl || '',
      freeVariantId: parseInt(t.freeVariantId) || 0,
      preMsg: (r) => `You're <strong>${r}</strong> away from ${t.title}!`,
      unlockedMsg: `${t.icon || '\ud83c\udf89'} ${t.title} Unlocked!`,
    }));
  }
  if (s.checkoutText) CD_CONFIG.CHECKOUT_BTN_TEXT = s.checkoutText;
  if (s.fbtCount) CD_CONFIG.FBT_PRODUCT_COUNT = s.fbtCount;
  if (s.shippingProtectionVariantId) CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID = parseInt(s.shippingProtectionVariantId);
  if (s.shippingProtectionPriceCents) CD_CONFIG.SHIPPING_PROTECTION_PRICE_CENTS = parseInt(s.shippingProtectionPriceCents);
  const applyVars = () => {
    const r = document.documentElement.style;
    if (s.drawerPrimary)     r.setProperty('--cd-primary', s.drawerPrimary);
    if (s.drawerSecondaryBg) r.setProperty('--cd-secondary', s.drawerSecondaryBg);
    if (s.drawerBg)          r.setProperty('--cd-bg', s.drawerBg);
    if (s.progressColor)     r.setProperty('--cd-progress-fill', s.progressColor);
    if (s.progressBgColor)   r.setProperty('--cd-progress-bg', s.progressBgColor);
    if (s.drawerWidth)       r.setProperty('--cd-width-desktop', s.drawerWidth + 'px');
    if (s.drawerRadius !== undefined) {
      r.setProperty('--cd-radius', s.drawerRadius + 'px');
      r.setProperty('--cd-radius-sm', Math.max(0, s.drawerRadius - 4) + 'px');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyVars);
  else applyVars();
})();

class CartDrawer {
  constructor() {
    this.drawer           = document.getElementById('cart-drawer');
    this.overlay          = document.getElementById('cart-drawer-overlay');
    this.closeBtn         = document.getElementById('cart-drawer-close');
    this.itemsContainer   = document.getElementById('cart-items-container');
    this.progressMsg      = document.getElementById('cart-progress-message');
    this.progressFill     = document.getElementById('cart-progress-fill');
    this.progressTrack    = document.getElementById('cart-progress-track');
    this.milestones       = document.getElementById('cart-progress-milestones');
    this.subtotalEl       = document.getElementById('cart-subtotal-price');
    this.checkoutBtn      = document.getElementById('cart-checkout-btn');
    this.checkoutText     = document.getElementById('cart-checkout-text');
    this.discountsWrap    = document.getElementById('cart-discounts');
    this.discountsInner   = document.getElementById('cart-discounts-inner');
    this.protectionToggle = document.getElementById('shipping-protection-toggle');
    this.fbtList          = document.getElementById('cart-fbt-list');
    this._fbtCache = {}; this._fbtPrefetching = {};
    this.isOpen = false; this.cart = null; this.lastProductId = null;
    this._addingGift = false; this._removingGift = false; this._pendingQty = {};
    this._init();
  }

  _init() {
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
    this.itemsContainer?.addEventListener('click', (e) => { const btn = e.target.closest('[data-action]'); if (btn) this._optimisticQty(btn.dataset.key, btn.dataset.action); });
    this.itemsContainer?.addEventListener('click', (e) => { const btn = e.target.closest('.cd-item__remove'); if (btn) this._optimisticRemove(btn.dataset.key); });
    this.protectionToggle?.addEventListener('change', () => this._optimisticProtection(this.protectionToggle.checked));
    document.addEventListener('cart:refresh', () => this._fetchCart());
    this._buildMilestones();
    this._fetchCart();
    const s = window.cdSectionSettings;
    if (s) {
      const cbtn = document.getElementById('cart-checkout-btn');
      if (cbtn) { cbtn.style.background = s.checkoutBg; cbtn.style.color = s.checkoutTextColor; }
      const pt = document.querySelector('.cd-protection__title');
      const pd = document.querySelector('.cd-protection__desc');
      const pp = document.querySelector('.cd-protection__price');
      if (pt) pt.textContent = s.protectionTitle;
      if (pd) pd.textContent = s.protectionDesc;
      if (pp) pp.textContent = s.protectionPrice;
    }
    const claimIcons = () => {
      ['#cart-icon-bubble', '.header__icon--cart', 'a[href="/cart"]'].forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          if (el.dataset.cdClaimed) return;
          el.dataset.cdClaimed = '1';
          el.addEventListener('mouseenter', () => this._prefetchFBTFromCart(), { passive: true });
          el.addEventListener('touchstart', () => this._prefetchFBTFromCart(), { passive: true });
          el.addEventListener('click', (e) => { e.preventDefault(); e.stopImmediatePropagation(); window.cartDrawer?.open(); }, true);
        });
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', claimIcons); else claimIcons();
    setTimeout(claimIcons, 600);
    setTimeout(() => this._prefetchFBTFromCart(), 800);
    setTimeout(() => this._prefetchFBTFallback(), 3000);
  }

  open() { this.isOpen = true; this.drawer.classList.add('is-open'); this.overlay.classList.add('is-open'); this.drawer.setAttribute('aria-hidden','false'); this.overlay.setAttribute('aria-hidden','false'); document.body.style.overflow = 'hidden'; this.closeBtn?.focus(); this._fetchCart(); }
  close() { this.isOpen = false; this.drawer.classList.remove('is-open'); this.overlay.classList.remove('is-open'); this.drawer.setAttribute('aria-hidden','true'); this.overlay.setAttribute('aria-hidden','true'); document.body.style.overflow = ''; }

  _optimisticQty(key, action) {
    if (!this.cart) return;
    const item = this.cart.items.find(i => i.key === key); if (!item) return;
    const newQty = action === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
    if (newQty === 0) { this.cart = { ...this.cart, items: this.cart.items.filter(i => i.key !== key) }; }
    else { this.cart = { ...this.cart, items: this.cart.items.map(i => i.key === key ? { ...i, quantity: newQty, final_line_price: i.final_price * newQty, original_line_price: i.original_price * newQty } : i) }; }
    this._updateTotal(); this._render(this.cart);
    this._pendingQty[key] = newQty;
    fetch('/cart/change.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:key, quantity:newQty }) })
      .then(r => r.json()).then(cart => { delete this._pendingQty[key]; this.cart = cart; this._render(cart); })
      .catch(() => { delete this._pendingQty[key]; this._fetchCart(); });
  }

  _optimisticRemove(key) {
    if (!this.cart) return;
    this.cart = { ...this.cart, items: this.cart.items.filter(i => i.key !== key) };
    this._updateTotal(); this._render(this.cart);
    fetch('/cart/change.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:key, quantity:0 }) })
      .then(r => r.json()).then(cart => { this.cart = cart; this._render(cart); })
      .catch(() => this._fetchCart());
  }

  _optimisticProtection(enable) {
    if (!this.cart) return;
    const protCents = CD_CONFIG.SHIPPING_PROTECTION_PRICE_CENTS;
    const vid = CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID;
    if (enable) { this.cart = { ...this.cart, total_price: this.cart.total_price + protCents, items: [...this.cart.items, { variant_id: vid, key: 'prot-optimistic', final_line_price: protCents, original_line_price: protCents, final_price: protCents, original_price: protCents, quantity: 1 }] }; }
    else { this.cart = { ...this.cart, total_price: Math.max(0, this.cart.total_price - protCents), items: this.cart.items.filter(i => i.variant_id !== vid) }; }
    this._updateTotal();
    const apiCall = enable
      ? fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:vid, quantity:1 }) })
      : (async () => { const c = await (await fetch('/cart.js')).json(); const item = c.items.find(i => i.variant_id === vid); if (item) return fetch('/cart/change.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:item.key, quantity:0 }) }); })();
    apiCall.then(() => fetch('/cart.js')).then(r => r.json()).then(cart => { this.cart = cart; this._render(cart); })
      .catch(() => { if (this.protectionToggle) this.protectionToggle.checked = !enable; this._fetchCart(); });
  }

  _optimisticFBTAdd(btn, variantId) {
    btn.disabled = true; btn.textContent = '\u2713 Added'; btn.style.background = '#4CAF50';
    fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:variantId, quantity:1 }) })
      .then(r => { if (!r.ok) throw new Error('fail'); return this._fetchCart(); })
      .catch(() => { btn.disabled = false; btn.textContent = 'Add'; btn.style.background = ''; });
  }

  async _fetchCart() {
    try { const res = await fetch('/cart.js'); const cart = await res.json(); this.cart = cart; this._render(cart); }
    catch (err) { console.error('[CartDrawer] fetch error:', err); }
  }

  _updateTotal() {
    if (!this.cart) return;
    const f = this._money(this.cart.total_price);
    if (this.subtotalEl) this.subtotalEl.textContent = f;
    if (this.checkoutText) this.checkoutText.innerHTML = CD_CONFIG.CHECKOUT_BTN_TEXT.replace('{total}', f);
  }

  _render(cart) {
    const items = cart.items.filter(i => i.variant_id !== CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID);
    const hasProtection = cart.items.some(i => i.variant_id === CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID);
    this._renderItems(items); this._renderProgress(cart); this._renderDiscounts(cart);
    this._renderProtection(hasProtection); this._renderTotal(cart);
    if (items.length > 0) {
      const pid = items[items.length - 1].product_id;
      if (pid !== this.lastProductId) { this.lastProductId = pid; this._prefetchFBTForProduct(pid); }
      this._loadFBT(pid);
    } else {
      if (this.fbtList) this.fbtList.innerHTML = '';
      const fbtSec = document.getElementById('cart-fbt-section'); if (fbtSec) fbtSec.style.display = 'none';
    }
  }

  _renderItems(items) {
    if (!this.itemsContainer) return;
    const protEl = document.getElementById('cart-shipping-protection');
    const fbtEl  = document.getElementById('cart-fbt-section');
    const footEl = document.getElementById('cart-drawer-footer');
    if (items.length === 0) {
      this.itemsContainer.innerHTML = this._emptyHTML();
      if (protEl) protEl.style.display = 'none'; if (fbtEl) fbtEl.style.display = 'none'; if (footEl) footEl.style.display = 'none';
      return;
    }
    if (protEl) protEl.style.display = ''; if (fbtEl) fbtEl.style.display = ''; if (footEl) footEl.style.display = '';
    this.itemsContainer.innerHTML = items.map((item, idx) => this._itemHTML(item, idx)).join('');
  }

  _emptyHTML() {
    return `<div class="cd-empty"><div class="cd-empty__illustration"><svg width="80" height="80" viewBox="0 0 80 80" fill="none"><circle cx="40" cy="40" r="38" stroke="#E8D5D0" stroke-width="2"/><path d="M24 28h4l4 20h16l4-16H30" stroke="#BC7D6A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="36" cy="52" r="2" fill="#BC7D6A"/><circle cx="46" cy="52" r="2" fill="#BC7D6A"/><path d="M36 34c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#BC7D6A" stroke-width="1.5" stroke-linecap="round"/></svg></div><p class="cd-empty__title">Your cart is empty</p><p class="cd-empty__subtitle">Add something beautiful to get started.</p><a href="${CD_CONFIG.CONTINUE_SHOPPING_URL}" class="cd-empty__cta">Shop Now</a></div>`;
  }

  _itemHTML(item, itemIndex = 0) {
    const lineFinal = item.final_line_price, lineOriginal = item.original_line_price;
    const hasDiscount = lineOriginal > lineFinal, lineSavings = lineOriginal - lineFinal;
    const imgSrc = item.image ? item.image.replace(/(\.\w+)$/, '_160x160$1') : null;
    const noDefault = !item.variant_title || item.variant_title === 'Default Title';
    const tags = (item.discounts || []).map(d => `<li class="cd-item__discount-tag">${this._esc(d.title)}</li>`).join('');
    return `<div class="cd-item" data-key="${item.key}" data-variant-id="${item.variant_id}" id="cart-item-${item.key}">
      <a href="${item.url||'#'}" class="cd-item__image-link" tabindex="-1" aria-hidden="true"><div class="cd-item__image-wrap">${imgSrc?`<img src="${imgSrc}" alt="${this._esc(item.title)}" class="cd-item__image" width="80" height="80" loading="${itemIndex<2?'eager':'lazy'}">` : `<div class="cd-item__image cd-item__image--placeholder"></div>`}</div></a>
      <div class="cd-item__details">
        <a href="${item.url||'#'}" class="cd-item__title">${this._esc(item.product_title)}</a>
        ${!noDefault?`<p class="cd-item__variant">${this._esc(item.variant_title)}</p>`:''}
        <div class="cd-item__pricing">${hasDiscount?`<span class="cd-item__price cd-item__price--compare">${this._money(lineOriginal)}</span><span class="cd-item__price cd-item__price--sale">${this._money(lineFinal)}</span><span class="cd-item__savings">Save ${this._money(lineSavings)}</span>`:`<span class="cd-item__price">${this._money(lineFinal)}</span>`}</div>
        ${tags?`<ul class="cd-item__discounts">${tags}</ul>`:''}
        <div class="cd-item__actions">
          <div class="cd-qty" data-key="${item.key}">
            <button class="cd-qty__btn cd-qty__btn--minus" type="button" data-action="decrease" data-key="${item.key}" ${item.quantity<=1?'disabled':''}><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7h12" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg></button>
            <span class="cd-qty__value">${item.quantity}</span>
            <button class="cd-qty__btn cd-qty__btn--plus" type="button" data-action="increase" data-key="${item.key}"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg></button>
          </div>
          <button class="cd-item__remove" type="button" data-key="${item.key}"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1M6 7v5M10 7v5M3 4l1 9a1 1 0 001 1h6a1 1 0 001-1l1-9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        </div>
      </div>
    </div>`;
  }

  _buildMilestones() {
    if (!this.milestones) return;
    const tiers = CD_CONFIG.REWARD_TIERS; if (!tiers||!tiers.length) return;
    const max = tiers[tiers.length-1].threshold;
    this.milestones.innerHTML = tiers.map(t => {
      const pct = (t.threshold/max)*100;
      const iconHTML = t.imageUrl ? `<img src="${t.imageUrl}" alt="" style="width:22px;height:22px;object-fit:cover;border-radius:50%;display:block;">` : `<span style="font-size:13px;line-height:1;">${t.icon}</span>`;
      return `<div class="cd-milestone" data-threshold="${t.threshold}" style="position:absolute;top:50%;left:${pct}%;transform:translate(-50%,-50%);display:flex;flex-direction:column;align-items:center;"><div class="cd-milestone__icon" style="width:26px;height:26px;border-radius:50%;background:#fff;border:2px solid #EDE0DC;display:flex;align-items:center;justify-content:center;overflow:hidden;position:relative;z-index:3;flex-shrink:0;">${iconHTML}</div><span class="cd-milestone__label" style="position:absolute;top:calc(100% + 5px);font-size:9px;font-weight:500;color:#7A6760;text-align:center;left:50%;transform:translateX(-50%);line-height:1.3;width:52px;white-space:normal;word-wrap:break-word;">${this._esc(t.title)}</span></div>`;
    }).join('');
  }

  _renderProgress(cart) {
    const tiers = CD_CONFIG.REWARD_TIERS; if (!tiers||!tiers.length) return;
    const maxTier = tiers[tiers.length-1];
    const freeIds = new Set(tiers.map(t=>t.freeVariantId).filter(v=>v>0));
    const spend = Math.max(0, cart.items.filter(i=>!freeIds.has(i.variant_id)&&i.variant_id!==CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID).reduce((s,i)=>s+i.final_line_price,0));
    const pct = Math.min(100,(spend/maxTier.threshold)*100);
    if (this.progressFill) this.progressFill.style.width = pct.toFixed(2)+'%';
    if (this.progressTrack) this.progressTrack.setAttribute('aria-valuenow',Math.round(pct));
    document.querySelectorAll('.cd-milestone').forEach(m => {
      const t=parseInt(m.dataset.threshold,10),u=spend>=t;
      const ic=m.querySelector('.cd-milestone__icon'),lb=m.querySelector('.cd-milestone__label');
      if(ic){ic.style.borderColor=u?'#BC7D6A':'#EDE0DC';ic.style.background=u?'#BC7D6A':'#fff';}
      if(lb){lb.style.color=u?'#BC7D6A':'#7A6760';lb.style.fontWeight=u?'600':'500';}
    });
    tiers.forEach(t => {
      if(!t.freeVariantId) return;
      const unlocked=spend>=t.threshold,inCart=cart.items.some(i=>i.variant_id===t.freeVariantId);
      if(unlocked&&!inCart) this._addFreeGift(t.freeVariantId);
      if(!unlocked&&inCart){const item=cart.items.find(i=>i.variant_id===t.freeVariantId);if(item)this._removeFreeGift(item.key);}
    });
    let msg='',unlocked=false;
    if(spend>=maxTier.threshold){msg='\ud83c\udf89 All rewards unlocked \u2014 you\'re amazing!';unlocked=true;}
    else{for(let i=0;i<tiers.length;i++){if(spend<tiers[i].threshold){const rem=tiers[i].threshold-spend;if(i>0&&spend>=tiers[i-1].threshold){unlocked=true;msg=tiers[i-1].unlockedMsg+` &mdash; ${tiers[i].preMsg(this._money(rem))}`;}else msg=tiers[i].preMsg(this._money(rem));break;}}}
    if(this.progressMsg){this.progressMsg.innerHTML=msg;this.progressMsg.style.color=unlocked?'#BC7D6A':'#2C1F1A';this.progressMsg.style.fontWeight=unlocked?'600':'500';}
  }

  async _addFreeGift(variantId) {
    if(this._addingGift)return;this._addingGift=true;
    try{await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:variantId,quantity:1,properties:{_free_gift:'true'}})});const c=await(await fetch('/cart.js')).json();const item=c.items.find(i=>i.variant_id===variantId);if(item)await fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:item.key,quantity:1,price:0})});await this._fetchCart();}catch(e){console.error('[CartDrawer] Free gift error:',e);}finally{this._addingGift=false;}
  }

  async _removeFreeGift(key) {
    if(this._removingGift)return;this._removingGift=true;
    try{await fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:key,quantity:0})});await this._fetchCart();}catch(e){console.error('[CartDrawer] Remove gift error:',e);}finally{this._removingGift=false;}
  }

  _renderDiscounts(cart) {
    if(!this.discountsWrap||!this.discountsInner)return;
    const codes=cart.cart_level_discount_applications||[];
    if(!codes.length){this.discountsWrap.style.display='none';return;}
    this.discountsWrap.style.display='';
    this.discountsInner.innerHTML=codes.map(d=>`<span class="cd-discount-badge">${this._esc(d.title)}<span class="cd-discount-badge__amount">\u2212${this._money(d.total_allocated_amount)}</span></span>`).join('');
  }

  _renderProtection(isActive){if(this.protectionToggle)this.protectionToggle.checked=isActive;}
  _renderTotal(cart){const f=this._money(cart.total_price);if(this.subtotalEl)this.subtotalEl.textContent=f;if(this.checkoutText)this.checkoutText.innerHTML=CD_CONFIG.CHECKOUT_BTN_TEXT.replace('{total}',f);}

  _fbtUrl(pid){return `/recommendations/products.json?product_id=${pid}&limit=${CD_CONFIG.FBT_PRODUCT_COUNT}&intent=${CD_CONFIG.FBT_INTENT}`;}
  async _prefetchFBTForProduct(pid){if(this._fbtCache[pid]||this._fbtPrefetching[pid])return;this._fbtPrefetching[pid]=true;try{this._fbtCache[pid]=await(await fetch(this._fbtUrl(pid))).json();}catch(e){}finally{delete this._fbtPrefetching[pid];}}
  async _prefetchFBTFromCart(){try{const c=await(await fetch('/cart.js')).json();const items=c.items.filter(i=>i.variant_id!==CD_CONFIG.SHIPPING_PROTECTION_VARIANT_ID);if(items.length)this._prefetchFBTForProduct(items[items.length-1].product_id);}catch(e){}}
  async _prefetchFBTFallback(){const pid=window.__cd_page_product_id||window.meta?.product?.id||(document.querySelector('[data-product-id]')&&parseInt(document.querySelector('[data-product-id]').dataset.productId));if(pid)this._prefetchFBTForProduct(pid);}

  async _loadFBT(pid) {
    const sec=document.getElementById('cart-fbt-section');if(!sec||!this.fbtList)return;
    sec.style.display='';
    if(this._fbtCache[pid]){this._renderFBT(pid);return;}
    this.fbtList.innerHTML=this._skelHTML();
    if(!this._fbtPrefetching[pid])this._prefetchFBTForProduct(pid);
    let n=0;const poll=setInterval(()=>{n++;if(this._fbtCache[pid]){clearInterval(poll);this._renderFBT(pid);}else if(n>30){clearInterval(poll);sec.style.display='none';}},100);
  }

  _skelHTML(){return Array(CD_CONFIG.FBT_PRODUCT_COUNT).fill(0).map(()=>`<div class="cd-fbt-card cd-fbt-card--skeleton"><div class="cd-fbt-skeleton__img"></div><div class="cd-fbt-skeleton__info"><div class="cd-fbt-skeleton__line cd-fbt-skeleton__line--title"></div><div class="cd-fbt-skeleton__line cd-fbt-skeleton__line--price"></div></div><div class="cd-fbt-skeleton__btn"></div></div>`).join('');}

  _renderFBT(pid) {
    const sec=document.getElementById('cart-fbt-section'),data=this._fbtCache[pid];
    if(!sec||!this.fbtList||!data)return;
    const inCart=new Set((this.cart?.items||[]).map(i=>i.product_id));
    const recs=(data.products||[]).filter(p=>!inCart.has(p.id));
    if(!recs.length){sec.style.display='none';return;}
    this.fbtList.innerHTML=recs.map(p=>this._fbtCard(p)).join('');
    this.fbtList.querySelectorAll('[data-fbt-add]').forEach(btn=>btn.addEventListener('click',()=>this._optimisticFBTAdd(btn,parseInt(btn.dataset.fbtAdd,10))));
  }

  _fbtCard(product) {
    const v=product.variants?.[0];if(!v)return '';
    const priceStr=this._money(parseInt(v.price,10));
    const img=product.featured_image?product.featured_image.replace(/(\.[^.]+)(\?|$)/,'_120x120$1$2'):null;
    return `<div class="cd-fbt-card">${img?`<img src="${img}" alt="${this._esc(product.title)}" class="cd-fbt-card__image" width="52" height="52" loading="eager" decoding="async">`:`<div class="cd-fbt-card__image"></div>`}<div class="cd-fbt-card__info"><p class="cd-fbt-card__title">${this._esc(product.title)}</p><p class="cd-fbt-card__price">${priceStr}</p></div><button class="cd-fbt-card__btn" type="button" data-fbt-add="${v.id}">Add</button></div>`;
  }

  _money(cents){if(cents===undefined||cents===null)return '';return '$'+(parseInt(cents,10)/100).toFixed(2);}
  _esc(s){if(!s)return '';return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');}
}

document.addEventListener('DOMContentLoaded',()=>{window.cartDrawer=new CartDrawer();document.dispatchEvent(new Event('cartDrawerReady'));});
document.addEventListener('DOMContentLoaded',()=>{
  document.querySelectorAll('a[href="/cart"],[data-cart-drawer-trigger],.header__icon--cart,#cart-icon-bubble').forEach(el=>el.addEventListener('click',(e)=>{e.preventDefault();window.cartDrawer?.open();}));
  document.addEventListener('shopify:cart:add',()=>{window.cartDrawer?.open();});
});
